import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { EnvelopeSimple, Check } from "@phosphor-icons/react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

export default function Newsletter() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [selectedRegions, setSelectedRegions] = useState(["global"]);
  const [loading, setLoading] = useState(false);
  const [subscribed, setSubscribed] = useState(false);

  const regions = [
    { id: "global", label: "All Markets" },
    { id: "ireland", label: "Ireland" },
    { id: "uk", label: "UK" },
    { id: "usa", label: "USA" },
  ];

  const toggleRegion = (regionId) => {
    setSelectedRegions((prev) =>
      prev.includes(regionId)
        ? prev.filter((r) => r !== regionId)
        : [...prev, regionId]
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, {
        email,
        name: name || undefined,
        regions: selectedRegions,
      });
      setSubscribed(true);
      toast.success("Successfully subscribed to Farmwatch newsletter!");
    } catch (error) {
      toast.error("Failed to subscribe. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (subscribed) {
    return (
      <section id="newsletter" className="py-16 bg-[#111827]">
        <div className="max-w-[600px] mx-auto px-4 text-center">
          <div className="w-16 h-16 mx-auto mb-6 bg-[#0F766E] flex items-center justify-center">
            <Check size={32} className="text-white" />
          </div>
          <h2 className="font-serif text-3xl text-white mb-4">
            You're Subscribed!
          </h2>
          <p className="font-sans text-[#8B8580]">
            Thank you for subscribing to Farmwatch. You'll receive the latest farming news and market updates in your inbox.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="newsletter" className="py-16 bg-[#111827]">
      <div className="max-w-[600px] mx-auto px-4">
        <div className="text-center mb-8">
          <div className="w-12 h-12 mx-auto mb-4 border border-[#0F766E] flex items-center justify-center">
            <EnvelopeSimple size={24} className="text-[#0F766E]" />
          </div>
          <h2 className="font-serif text-3xl text-white mb-3">
            Stay Informed
          </h2>
          <p className="font-sans text-[#8B8580]">
            Get the latest farming news, market prices, and industry insights delivered to your inbox.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <input
              type="text"
              data-testid="newsletter-name"
              placeholder="Your name (optional)"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/50 font-sans focus:outline-none focus:border-[#0F766E]"
            />
            <input
              type="email"
              data-testid="newsletter-email"
              placeholder="Your email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 text-white placeholder-white/50 font-sans focus:outline-none focus:border-[#0F766E]"
            />
          </div>

          {/* Region Selection */}
          <div>
            <p className="text-xs font-mono uppercase tracking-wider text-[#8B8580] mb-3">
              Select your markets of interest
            </p>
            <div className="flex flex-wrap gap-2">
              {regions.map((region) => (
                <button
                  key={region.id}
                  type="button"
                  data-testid={`newsletter-region-${region.id}`}
                  onClick={() => toggleRegion(region.id)}
                  className={`px-4 py-2 text-sm font-sans transition-colors ${
                    selectedRegions.includes(region.id)
                      ? "bg-[#0F766E] text-white"
                      : "bg-white/10 text-white/70 hover:bg-white/20"
                  }`}
                >
                  {region.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="submit"
            data-testid="newsletter-submit"
            disabled={loading}
            className="w-full py-3 bg-white text-[#33302E] font-sans font-medium hover:bg-[#0F766E] hover:text-white transition-colors disabled:opacity-50"
          >
            {loading ? "Subscribing..." : "Subscribe to Newsletter"}
          </button>

          <p className="text-xs text-center text-[#8B8580] font-sans">
            By subscribing, you agree to receive marketing emails from Farmwatch. Unsubscribe anytime.
          </p>
        </form>
      </div>
    </section>
  );
}
